import differenceInYears from 'date-fns/differenceInYears';
import getYear from 'date-fns/getYear';
import formatDistanceStrict from 'date-fns/formatDistanceStrict';
import _upperFirst from 'lodash/upperFirst';
import formatCurrency from './formatCurrency';

function getNameComponents(name = '') {
  const split = name.split('.');
  return [split.shift(), split.join('.')];
}

export default function getSalePresenter(sale) {
  return {
    ...(sale?.product ?? {}),
    ...sale,

    get ageYears() {
      return sale.product?.registeredDate
        ? differenceInYears(new Date(), new Date(sale.product.registeredDate))
        : null;
    },

    get ageYearsFormatted() {
      if (this.ageYears === 0) {
        return 'Less than a year old';
      }
      return `${this.ageYears} ${this.ageYears === 1 ? 'year' : 'years'} old`;
    },

    get bids() {
      return [...(sale.bids || [])]
        .sort((a, b) => new Date(b.index) - new Date(a.index))
        .filter((bid, index) => {
          // Hide consecutive same-user bids for users other than self
          // This happens when a user ups their max bid by bidding consecutively
          const previousBid = sale.bids[index - 1];
          if (!bid.user.isSelf && previousBid?.user.id === bid.user.id) {
            return false;
          }
          return true;
        });
    },

    get isEnded() {
      if (!sale.endDate) {
        return false;
      }
      return new Date(sale.endDate) < new Date();
    },

    get isRecentlyEnded() {
      return new Date(sale.endDate) >= new Date() - 60 * 60 * 1000;
    },

    get isEndingSoon() {
      const now = new Date().getTime();
      const endDate = new Date(sale.endDate).getTime();
      const diffMs = endDate - now;
      return diffMs > 0 && diffMs < 60 * 60 * 1000; // less than one hour left
    },

    get minBidFormatted() {
      if (sale.minBid) {
        return formatCurrency(sale.minBid);
      }
      return null;
    },

    get nameFormatted() {
      const components = getNameComponents(sale.product?.name);
      return (
        <>
          {components[0] + '.'}
          <span className="tw-inline-block">{components[1]}</span>
        </>
      );
    },

    get nameLength() {
      return sale.product?.name.split('.')[0].length;
    },

    get permalink() {
      if (!sale.id) {
        throw new Error('Attempted to get permalink for sale without id');
      }
      if (this.saleType === 'buy') {
        return `/buynow/${sale.product.name}/`;
      }
      if (this.isEnded) {
        return `/sale/${sale.id}/${sale.product.name}/`;
      }
      return `/${sale.product.name}/`;
    },

    get priceFormatted() {
      if (sale.price) {
        return formatCurrency(sale.price);
      }
      return null;
    },

    get renewPriceFormatted() {
      if (sale.renewPrice) {
        return formatCurrency(sale.renewPrice);
      }
      return null;
    },

    get statusFormatted() {
      if (!['active', 'ended'].includes(sale.status)) {
        return _upperFirst(sale.status);
      }
      return '';
    },

    get timeLeft() {
      if (!sale.endDate) return '';
      const now = new Date();
      const endDate = new Date(sale.endDate);
      if (endDate < now) {
        return 'Ended';
      }
      return formatDistanceStrict(endDate, now);
    },

    get tld() {
      const components = getNameComponents(sale.product?.name);
      return components[1];
    },

    get yearRegistered() {
      return sale.product?.registeredDate
        ? getYear(new Date(sale.product.registeredDate))
        : '';
    },
  };
}
